import React, { useState } from 'react';
import {
  Box, VStack, HStack, Button, Text, Heading, Card, CardBody,
  FormControl, FormLabel, Input, Textarea, Select, Switch,
  Icon, Badge, Alert, AlertIcon, Divider, Code, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, Accordion,
  AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, Flex, Spacer,
  NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper, Slider,
  SliderTrack, SliderFilledTrack, SliderThumb, SliderMark,
  Checkbox, CheckboxGroup, Radio, RadioGroup, Stack
} from '@chakra-ui/react';
import { 
  FaPlus, FaEdit, FaTrash, FaPlay, FaSave, FaCode, FaCopy, 
  FaDownload, FaUpload, FaTools, FaCheckCircle, FaTimesCircle,
  FaClock, FaServer, FaExclamationTriangle, FaChartLine,
  FaShieldAlt, FaRobot, FaLink, FaDatabase, FaCog // Added FaCog import
} from 'react-icons/fa';

const FunctionBuilder = ({ 
  availableFunctions, 
  setAvailableFunctions,
  testFunction 
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingFunction, setEditingFunction] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [isTestMode, setIsTestMode] = useState(false);
  const toast = useToast();

  // Enhanced form state with Vapi-specific features
  const [functionForm, setFunctionForm] = useState({
    name: '',
    description: '',
    enabled: true,
    color: 'blue',
    parameters: {
      type: "object",
      properties: {},
      required: []
    },
    implementation: '',
    testCases: [],
    // Vapi-specific advanced features
    async: false,
    timeout: 30000, // 30 seconds default
    retries: 1,
    webhook: {
      enabled: false,
      url: '',
      method: 'POST',
      headers: {},
      timeout: 10000
    },
    validation: {
      enabled: false,
      rules: []
    },
    responseHandling: {
      successMessage: 'Function completed successfully',
      errorMessage: 'Function failed to execute',
      speakResult: true,
      formatResponse: true
    },
    analytics: {
      trackUsage: true,
      trackPerformance: true,
      customEvents: []
    },
    security: {
      requireAuth: false,
      rateLimitPerMinute: 60,
      allowedDomains: []
    },
    ai: {
      contextAware: false,
      learningEnabled: false,
      conversationMemory: false
    }
  });

  const [parameterForm, setParameterForm] = useState({
    name: '',
    type: 'string',
    description: '',
    required: false,
    enum: '',
    format: '',
    // Advanced parameter features
    defaultValue: '',
    minLength: '',
    maxLength: '',
    pattern: '',
    minimum: '',
    maximum: '',
    multipleOf: '',
    examples: []
  });

  const resetForm = () => {
    setFunctionForm({
      name: '',
      description: '',
      enabled: true,
      color: 'blue',
      parameters: { type: "object", properties: {}, required: [] },
      implementation: '',
      testCases: [],
      async: false,
      timeout: 30000,
      retries: 1,
      webhook: {
        enabled: false,
        url: '',
        method: 'POST',
        headers: {},
        timeout: 10000
      },
      validation: {
        enabled: false,
        rules: []
      },
      responseHandling: {
        successMessage: 'Function completed successfully',
        errorMessage: 'Function failed to execute',
        speakResult: true,
        formatResponse: true
      },
      analytics: {
        trackUsage: true,
        trackPerformance: true,
        customEvents: []
      },
      security: {
        requireAuth: false,
        rateLimitPerMinute: 60,
        allowedDomains: []
      },
      ai: {
        contextAware: false,
        learningEnabled: false,
        conversationMemory: false
      }
    });
    setParameterForm({
      name: '', type: 'string', description: '', required: false, enum: '', format: '',
      defaultValue: '', minLength: '', maxLength: '', pattern: '', minimum: '', maximum: '',
      multipleOf: '', examples: []
    });
    setEditingFunction(null);
  };

  const openEditModal = (func = null) => {
    if (func) {
      setEditingFunction(func);
      setFunctionForm({
        ...func,
        // Ensure all new fields have defaults if not present
        async: func.async || false,
        timeout: func.timeout || 30000,
        retries: func.retries || 1,
        webhook: func.webhook || {
          enabled: false,
          url: '',
          method: 'POST',
          headers: {},
          timeout: 10000
        },
        validation: func.validation || {
          enabled: false,
          rules: []
        },
        responseHandling: func.responseHandling || {
          successMessage: 'Function completed successfully',
          errorMessage: 'Function failed to execute',
          speakResult: true,
          formatResponse: true
        },
        analytics: func.analytics || {
          trackUsage: true,
          trackPerformance: true,
          customEvents: []
        },
        security: func.security || {
          requireAuth: false,
          rateLimitPerMinute: 60,
          allowedDomains: []
        },
        ai: func.ai || {
          contextAware: false,
          learningEnabled: false,
          conversationMemory: false
        }
      });
    } else {
      resetForm();
    }
    onOpen();
  };

  const addParameter = () => {
    if (!parameterForm.name) {
      toast({ title: 'Parameter name is required', status: 'error', duration: 3000 });
      return;
    }

    const newParam = {
      type: parameterForm.type,
      description: parameterForm.description
    };

    // Add advanced parameter properties
    if (parameterForm.enum) {
      newParam.enum = parameterForm.enum.split(',').map(s => s.trim());
    }
    if (parameterForm.format) newParam.format = parameterForm.format;
    if (parameterForm.defaultValue) newParam.default = parameterForm.defaultValue;
    if (parameterForm.pattern) newParam.pattern = parameterForm.pattern;
    
    // String constraints
    if (parameterForm.minLength) newParam.minLength = parseInt(parameterForm.minLength);
    if (parameterForm.maxLength) newParam.maxLength = parseInt(parameterForm.maxLength);
    
    // Number constraints
    if (parameterForm.minimum) newParam.minimum = parseFloat(parameterForm.minimum);
    if (parameterForm.maximum) newParam.maximum = parseFloat(parameterForm.maximum);
    if (parameterForm.multipleOf) newParam.multipleOf = parseFloat(parameterForm.multipleOf);
    
    // Examples for AI training
    if (parameterForm.examples.length > 0) {
      newParam.examples = parameterForm.examples;
    }

    const updatedParameters = {
      ...functionForm.parameters,
      properties: {
        ...functionForm.parameters.properties,
        [parameterForm.name]: newParam
      }
    };

    if (parameterForm.required) {
      updatedParameters.required = [
        ...functionForm.parameters.required,
        parameterForm.name
      ];
    }

    setFunctionForm(prev => ({ ...prev, parameters: updatedParameters }));
    setParameterForm({
      name: '', type: 'string', description: '', required: false, enum: '', format: '',
      defaultValue: '', minLength: '', maxLength: '', pattern: '', minimum: '', maximum: '',
      multipleOf: '', examples: []
    });

    toast({ 
      title: 'Parameter added', 
      description: `${parameterForm.name} parameter added successfully`,
      status: 'success', 
      duration: 2000 
    });
  };

  // Added missing removeParameter function
  const removeParameter = (paramName) => {
    const updatedProperties = { ...functionForm.parameters.properties };
    delete updatedProperties[paramName];

    const updatedRequired = functionForm.parameters.required.filter(name => name !== paramName);

    setFunctionForm(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        properties: updatedProperties,
        required: updatedRequired
      }
    }));

    toast({ title: 'Parameter removed', status: 'info', duration: 2000 });
  };

  const generateVapiFunctionConfig = () => {
    return {
      name: functionForm.name,
      description: functionForm.description,
      parameters: functionForm.parameters,
      async: functionForm.async,
      timeout: functionForm.timeout,
      retries: functionForm.retries,
      webhook: functionForm.webhook.enabled ? {
        url: functionForm.webhook.url,
        method: functionForm.webhook.method,
        headers: functionForm.webhook.headers,
        timeout: functionForm.webhook.timeout
      } : undefined,
      validation: functionForm.validation.enabled ? functionForm.validation.rules : undefined,
      response: {
        success_message: functionForm.responseHandling.successMessage,
        error_message: functionForm.responseHandling.errorMessage,
        speak_result: functionForm.responseHandling.speakResult,
        format_response: functionForm.responseHandling.formatResponse
      },
      security: {
        require_auth: functionForm.security.requireAuth,
        rate_limit: functionForm.security.rateLimitPerMinute,
        allowed_domains: functionForm.security.allowedDomains
      },
      ai_features: {
        context_aware: functionForm.ai.contextAware,
        learning_enabled: functionForm.ai.learningEnabled,
        conversation_memory: functionForm.ai.conversationMemory
      }
    };
  };

  // Rest of your existing functions remain the same...
  const saveFunction = () => {
    if (!functionForm.name || !functionForm.description) {
      toast({ title: 'Name and description are required', status: 'error', duration: 3000 });
      return;
    }

    const newFunction = {
      ...functionForm,
      icon: FaTools,
      isCustom: true,
      vapiConfig: generateVapiFunctionConfig()
    };

    if (editingFunction) {
      setAvailableFunctions(prev => 
        prev.map(f => f.name === editingFunction.name ? newFunction : f)
      );
      toast({ title: 'Function updated', status: 'success', duration: 3000 });
    } else {
      const exists = availableFunctions.some(f => f.name === functionForm.name);
      if (exists) {
        toast({ title: 'Function name already exists', status: 'error', duration: 3000 });
        return;
      }

      setAvailableFunctions(prev => [...prev, newFunction]);
      toast({ title: 'Function created', status: 'success', duration: 3000 });
    }

    onClose();
    resetForm();
  };

  const deleteFunction = (functionName) => {
    setAvailableFunctions(prev => prev.filter(f => f.name !== functionName));
    toast({ title: 'Function deleted', status: 'info', duration: 2000 });
  };

  const duplicateFunction = (func) => {
    const duplicatedFunction = {
      ...func,
      name: `${func.name}_copy_${Date.now()}`,
      isCustom: true
    };
    setAvailableFunctions(prev => [...prev, duplicatedFunction]);
    toast({ title: 'Function duplicated', status: 'success', duration: 2000 });
  };

  const exportFunctions = () => {
    const customFunctions = availableFunctions.filter(f => f.isCustom);
    const dataStr = JSON.stringify(customFunctions, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'custom_functions.json';
    link.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Functions exported', status: 'success', duration: 2000 });
  };

  const importFunctions = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const functions = JSON.parse(e.target.result);
        if (Array.isArray(functions)) {
          setAvailableFunctions(prev => [...prev, ...functions]);
          toast({ 
            title: 'Functions imported', 
            description: `${functions.length} functions added`,
            status: 'success', 
            duration: 3000 
          });
        }
      } catch (error) {
        toast({ title: 'Invalid file format', status: 'error', duration: 3000 });
      }
    };
    reader.readAsText(file);
  };

  // Template functions for common use cases
  const functionTemplates = [
    {
      name: 'webhook_caller',
      description: 'Make HTTP requests to external APIs',
      template: {
        async: true,
        timeout: 10000,
        webhook: {
          enabled: true,
          url: 'https://api.example.com/endpoint',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          timeout: 5000
        },
        parameters: {
          type: "object",
          properties: {
            endpoint: { type: "string", description: "API endpoint to call" },
            data: { type: "object", description: "Data to send in request body" }
          },
          required: ["endpoint"]
        }
      }
    },
    {
      name: 'database_query',
      description: 'Query database with validation',
      template: {
        async: true,
        timeout: 15000,
        validation: {
          enabled: true,
          rules: ['sanitize_sql', 'validate_permissions']
        },
        security: {
          requireAuth: true,
          rateLimitPerMinute: 10
        },
        parameters: {
          type: "object",
          properties: {
            query: { 
              type: "string", 
              description: "SQL query to execute",
              pattern: "^SELECT.*",
              maxLength: 1000
            },
            params: { 
              type: "array", 
              description: "Query parameters",
              maxItems: 10
            }
          },
          required: ["query"]
        }
      }
    },
    {
      name: 'ai_assistant',
      description: 'Context-aware AI helper function',
      template: {
        async: true,
        ai: {
          contextAware: true,
          learningEnabled: true,
          conversationMemory: true
        },
        responseHandling: {
          speakResult: true,
          formatResponse: true
        },
        parameters: {
          type: "object",
          properties: {
            query: { 
              type: "string", 
              description: "User query or request",
              examples: ["Help me with...", "What is...", "How do I..."]
            },
            context: { 
              type: "string", 
              description: "Additional context for the request"
            }
          },
          required: ["query"]
        }
      }
    }
  ];

  const applyTemplate = (template) => {
    setFunctionForm(prev => ({
      ...prev,
      ...template.template,
      name: template.name,
      description: template.description
    }));
    toast({ 
      title: 'Template Applied', 
      description: `${template.name} template loaded`,
      status: 'success', 
      duration: 2000 
    });
  };

  return (
    <VStack spacing={6} align="stretch">
      <Card>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Heading size="md">Advanced Function Builder</Heading>
              <HStack>
                <Switch
                  isChecked={isTestMode}
                  onChange={(e) => setIsTestMode(e.target.checked)}
                  colorScheme="purple"
                />
                <Text fontSize="sm">Test Mode</Text>
                <Button size="sm" onClick={() => openEditModal()} leftIcon={<Icon as={FaPlus} />}>
                  New Function
                </Button>
                <Button size="sm" onClick={exportFunctions} leftIcon={<Icon as={FaDownload} />}>
                  Export
                </Button>
                <Button as="label" size="sm" leftIcon={<Icon as={FaUpload} />}>
                  Import
                  <input type="file" hidden accept=".json" onChange={importFunctions} />
                </Button>
              </HStack>
            </HStack>

            {/* Function Templates */}
            <Card bg="blue.50">
              <CardBody p={4}>
                <Text fontWeight="bold" mb={3}>Quick Start Templates</Text>
                <HStack spacing={2} wrap="wrap">
                  {functionTemplates.map((template) => (
                    <Button
                      key={template.name}
                      size="sm"
                      variant="outline"
                      onClick={() => applyTemplate(template)}
                    >
                      {template.name}
                    </Button>
                  ))}
                </HStack>
              </CardBody>
            </Card>

            <Divider />

            {/* Enhanced Function List with more details */}
            <Box>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Function</Th>
                    <Th>Type</Th>
                    <Th>Features</Th>
                    <Th>Performance</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {availableFunctions.map((func) => (
                    <Tr key={func.name}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Icon as={func.icon || FaTools} color={`${func.color || 'blue'}.500`} />
                            <Text fontWeight="bold" fontSize="sm">{func.name}</Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.600" noOfLines={2}>
                            {func.description}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Badge colorScheme={func.isCustom ? 'purple' : 'gray'}>
                            {func.isCustom ? 'Custom' : 'Built-in'}
                          </Badge>
                          <Badge colorScheme={func.enabled ? 'green' : 'gray'} size="sm">
                            {func.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </VStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          {func.async && <Badge colorScheme="blue" size="xs">Async</Badge>}
                          {func.webhook?.enabled && <Badge colorScheme="orange" size="xs">Webhook</Badge>}
                          {func.validation?.enabled && <Badge colorScheme="red" size="xs">Validated</Badge>}
                          {func.ai?.contextAware && <Badge colorScheme="purple" size="xs">AI-Aware</Badge>}
                          {func.security?.requireAuth && <Badge colorScheme="red" size="xs">Auth Required</Badge>}
                        </VStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs">
                            Timeout: {(func.timeout || 30000) / 1000}s
                          </Text>
                          <Text fontSize="xs">
                            Retries: {func.retries || 1}
                          </Text>
                          {func.security?.rateLimitPerMinute && (
                            <Text fontSize="xs">
                              Rate: {func.security.rateLimitPerMinute}/min
                            </Text>
                          )}
                        </VStack>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          {isTestMode && (
                            <TestFunctionModal 
                              func={func}
                              onTest={(f, params) => console.log('Test:', f.name, params)}
                            />
                          )}
                          <IconButton 
                            size="xs" 
                            icon={<FaEdit />} 
                            onClick={() => openEditModal(func)}
                            variant="ghost"
                          />
                          {func.isCustom && (
                            <>
                              <IconButton 
                                size="xs" 
                                icon={<FaCopy />} 
                                onClick={() => duplicateFunction(func)}
                                variant="ghost"
                              />
                              <IconButton 
                                size="xs" 
                                icon={<FaTrash />} 
                                onClick={() => deleteFunction(func.name)}
                                variant="ghost"
                                colorScheme="red"
                              />
                            </>
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Enhanced Function Editor Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>
            {editingFunction ? 'Edit Function' : 'Create New Function'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Accordion defaultIndex={[0]} allowMultiple>
              {/* Basic Configuration */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaCog} mr={2} />
                    Basic Configuration
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>Function Name</FormLabel>
                        <Input
                          value={functionForm.name}
                          onChange={(e) => setFunctionForm(prev => ({...prev, name: e.target.value}))}
                          placeholder="e.g., send_notification"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Color</FormLabel>
                        <Select
                          value={functionForm.color}
                          onChange={(e) => setFunctionForm(prev => ({...prev, color: e.target.value}))}
                        >
                          <option value="blue">Blue</option>
                          <option value="green">Green</option>
                          <option value="purple">Purple</option>
                          <option value="orange">Orange</option>
                          <option value="red">Red</option>
                          <option value="teal">Teal</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Status</FormLabel>
                        <Switch
                          isChecked={functionForm.enabled}
                          onChange={(e) => setFunctionForm(prev => ({...prev, enabled: e.target.checked}))}
                          colorScheme="green"
                        />
                      </FormControl>
                    </HStack>

                    <FormControl>
                      <FormLabel>Description</FormLabel>
                      <Textarea
                        value={functionForm.description}
                        onChange={(e) => setFunctionForm(prev => ({...prev, description: e.target.value}))}
                        placeholder="Describe what this function does..."
                        rows={2}
                      />
                    </FormControl>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* Performance & Reliability */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaClock} mr={2} />
                    Performance & Reliability
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>Execution Type</FormLabel>
                        <RadioGroup
                          value={functionForm.async ? 'async' : 'sync'}
                          onChange={(value) => setFunctionForm(prev => ({...prev, async: value === 'async'}))}
                        >
                          <Stack direction="row">
                            <Radio value="sync">Synchronous</Radio>
                            <Radio value="async">Asynchronous</Radio>
                          </Stack>
                        </RadioGroup>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Timeout (seconds)</FormLabel>
                        <NumberInput
                          value={functionForm.timeout / 1000}
                          onChange={(_, value) => setFunctionForm(prev => ({...prev, timeout: value * 1000}))}
                          min={1}
                          max={300}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Retry Attempts</FormLabel>
                        <NumberInput
                          value={functionForm.retries}
                          onChange={(_, value) => setFunctionForm(prev => ({...prev, retries: value}))}
                          min={0}
                          max={10}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </HStack>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* Webhook Integration */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaServer} mr={2} />
                    Webhook Integration
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <HStack>
                        <Switch
                          isChecked={functionForm.webhook.enabled}
                          onChange={(e) => setFunctionForm(prev => ({
                            ...prev,
                            webhook: { ...prev.webhook, enabled: e.target.checked }
                          }))}
                          colorScheme="blue"
                        />
                        <FormLabel mb={0}>Enable Webhook</FormLabel>
                      </HStack>
                    </FormControl>

                    {functionForm.webhook.enabled && (
                      <>
                        <HStack spacing={4}>
                          <FormControl flex="3">
                            <FormLabel>Webhook URL</FormLabel>
                            <Input
                              value={functionForm.webhook.url}
                              onChange={(e) => setFunctionForm(prev => ({
                                ...prev,
                                webhook: { ...prev.webhook, url: e.target.value }
                              }))}
                              placeholder="https://api.example.com/webhook"
                            />
                          </FormControl>
                          <FormControl flex="1">
                            <FormLabel>Method</FormLabel>
                            <Select
                              value={functionForm.webhook.method}
                              onChange={(e) => setFunctionForm(prev => ({
                                ...prev,
                                webhook: { ...prev.webhook, method: e.target.value }
                              }))}
                            >
                              <option value="POST">POST</option>
                              <option value="PUT">PUT</option>
                              <option value="PATCH">PATCH</option>
                              <option value="GET">GET</option>
                            </Select>
                          </FormControl>
                        </HStack>

                        <FormControl>
                          <FormLabel>Custom Headers (JSON)</FormLabel>
                          <Textarea
                            value={JSON.stringify(functionForm.webhook.headers, null, 2)}
                            onChange={(e) => {
                              try {
                                const headers = JSON.parse(e.target.value);
                                setFunctionForm(prev => ({
                                  ...prev,
                                  webhook: { ...prev.webhook, headers }
                                }));
                              } catch (error) {
                                // Invalid JSON, ignore
                              }
                            }}
                            placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                            rows={3}
                          />
                        </FormControl>
                      </>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* Security Settings */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaShieldAlt} mr={2} />
                    Security Settings
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl>
                        <HStack>
                          <Switch
                            isChecked={functionForm.security.requireAuth}
                            onChange={(e) => setFunctionForm(prev => ({
                              ...prev,
                              security: { ...prev.security, requireAuth: e.target.checked }
                            }))}
                            colorScheme="red"
                          />
                          <FormLabel mb={0}>Require Authentication</FormLabel>
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Rate Limit (calls/minute)</FormLabel>
                        <NumberInput
                          value={functionForm.security.rateLimitPerMinute}
                          onChange={(_, value) => setFunctionForm(prev => ({
                            ...prev,
                            security: { ...prev.security, rateLimitPerMinute: value }
                          }))}
                          min={1}
                          max={1000}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    </HStack>

                    <FormControl>
                      <FormLabel>Allowed Domains (comma-separated)</FormLabel>
                      <Input
                        value={functionForm.security.allowedDomains.join(', ')}
                        onChange={(e) => setFunctionForm(prev => ({
                          ...prev,
                          security: { 
                            ...prev.security, 
                            allowedDomains: e.target.value.split(',').map(d => d.trim()).filter(Boolean) 
                          }
                        }))}
                        placeholder="example.com, api.example.com"
                      />
                    </FormControl>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* AI Features */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaRobot} mr={2} />
                    AI Enhancement Features
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    <CheckboxGroup>
                      <VStack align="start">
                        <Checkbox
                          isChecked={functionForm.ai.contextAware}
                          onChange={(e) => setFunctionForm(prev => ({
                            ...prev,
                            ai: { ...prev.ai, contextAware: e.target.checked }
                          }))}
                        >
                          Context Awareness - Function can access conversation context
                        </Checkbox>
                        <Checkbox
                          isChecked={functionForm.ai.learningEnabled}
                          onChange={(e) => setFunctionForm(prev => ({
                            ...prev,
                            ai: { ...prev.ai, learningEnabled: e.target.checked }
                          }))}
                        >
                          Learning Enabled - AI learns from function usage patterns
                        </Checkbox>
                        <Checkbox
                          isChecked={functionForm.ai.conversationMemory}
                          onChange={(e) => setFunctionForm(prev => ({
                            ...prev,
                            ai: { ...prev.ai, conversationMemory: e.target.checked }
                          }))}
                        >
                          Conversation Memory - Remember previous function calls
                        </Checkbox>
                      </VStack>
                    </CheckboxGroup>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* Response Handling */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaChartLine} mr={2} />
                    Response Handling
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    <HStack spacing={4}>
                      <FormControl flex="1">
                        <FormLabel>Success Message</FormLabel>
                        <Input
                          value={functionForm.responseHandling.successMessage}
                          onChange={(e) => setFunctionForm(prev => ({
                            ...prev,
                            responseHandling: { ...prev.responseHandling, successMessage: e.target.value }
                          }))}
                          placeholder="Function completed successfully"
                        />
                      </FormControl>
                      <FormControl flex="1">
                        <FormLabel>Error Message</FormLabel>
                        <Input
                          value={functionForm.responseHandling.errorMessage}
                          onChange={(e) => setFunctionForm(prev => ({
                            ...prev,
                            responseHandling: { ...prev.responseHandling, errorMessage: e.target.value }
                          }))}
                          placeholder="Function failed to execute"
                        />
                      </FormControl>
                    </HStack>

                    <HStack spacing={4}>
                      <FormControl>
                        <HStack>
                          <Switch
                            isChecked={functionForm.responseHandling.speakResult}
                            onChange={(e) => setFunctionForm(prev => ({
                              ...prev,
                              responseHandling: { ...prev.responseHandling, speakResult: e.target.checked }
                            }))}
                            colorScheme="green"
                          />
                          <FormLabel mb={0}>Speak Result</FormLabel>
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <HStack>
                          <Switch
                            isChecked={functionForm.responseHandling.formatResponse}
                            onChange={(e) => setFunctionForm(prev => ({
                              ...prev,
                              responseHandling: { ...prev.responseHandling, formatResponse: e.target.checked }
                            }))}
                            colorScheme="blue"
                          />
                          <FormLabel mb={0}>Format Response</FormLabel>
                        </HStack>
                      </FormControl>
                    </HStack>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* Enhanced Parameters Section */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaDatabase} mr={2} />
                    Parameters Configuration
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    {/* Add Parameter Form with advanced options */}
                    <Card bg="gray.50" mb={4}>
                      <CardBody p={4}>
                        <Text fontSize="sm" fontWeight="bold" mb={3}>Add Parameter</Text>
                        <VStack spacing={3}>
                          <HStack spacing={2} w="100%">
                            <Input
                              placeholder="Parameter name"
                              value={parameterForm.name}
                              onChange={(e) => setParameterForm(prev => ({...prev, name: e.target.value}))}
                              size="sm"
                            />
                            <Select
                              value={parameterForm.type}
                              onChange={(e) => setParameterForm(prev => ({...prev, type: e.target.value}))}
                              size="sm"
                            >
                              <option value="string">String</option>
                              <option value="number">Number</option>
                              <option value="integer">Integer</option>
                              <option value="boolean">Boolean</option>
                              <option value="array">Array</option>
                              <option value="object">Object</option>
                            </Select>
                            <Switch
                              isChecked={parameterForm.required}
                              onChange={(e) => setParameterForm(prev => ({...prev, required: e.target.checked}))}
                              size="sm"
                            />
                          </HStack>

                          <HStack spacing={2} w="100%">
                            <Input
                              placeholder="Description"
                              value={parameterForm.description}
                              onChange={(e) => setParameterForm(prev => ({...prev, description: e.target.value}))}
                              size="sm"
                              flex="2"
                            />
                            <Input
                              placeholder="Default value"
                              value={parameterForm.defaultValue}
                              onChange={(e) => setParameterForm(prev => ({...prev, defaultValue: e.target.value}))}
                              size="sm"
                              flex="1"
                            />
                          </HStack>

                          {/* Conditional fields based on parameter type */}
                          {parameterForm.type === 'string' && (
                            <HStack spacing={2} w="100%">
                              <Input
                                placeholder="Min Length"
                                value={parameterForm.minLength}
                                onChange={(e) => setParameterForm(prev => ({...prev, minLength: e.target.value}))}
                                size="sm"
                                type="number"
                              />
                              <Input
                                placeholder="Max Length"
                                value={parameterForm.maxLength}
                                onChange={(e) => setParameterForm(prev => ({...prev, maxLength: e.target.value}))}
                                size="sm"
                                type="number"
                              />
                              <Input
                                placeholder="Pattern (regex)"
                                value={parameterForm.pattern}
                                onChange={(e) => setParameterForm(prev => ({...prev, pattern: e.target.value}))}
                                size="sm"
                                flex="2"
                              />
                            </HStack>
                          )}

                          {(parameterForm.type === 'number' || parameterForm.type === 'integer') && (
                            <HStack spacing={2} w="100%">
                              <Input
                                placeholder="Minimum"
                                value={parameterForm.minimum}
                                onChange={(e) => setParameterForm(prev => ({...prev, minimum: e.target.value}))}
                                size="sm"
                                type="number"
                              />
                              <Input
                                placeholder="Maximum"
                                value={parameterForm.maximum}
                                onChange={(e) => setParameterForm(prev => ({...prev, maximum: e.target.value}))}
                                size="sm"
                                type="number"
                              />
                              <Input
                                placeholder="Multiple of"
                                value={parameterForm.multipleOf}
                                onChange={(e) => setParameterForm(prev => ({...prev, multipleOf: e.target.value}))}
                                size="sm"
                                type="number"
                              />
                            </HStack>
                          )}

                          <HStack spacing={2} w="100%">
                            <Input
                              placeholder="Enum values (comma-separated)"
                              value={parameterForm.enum}
                              onChange={(e) => setParameterForm(prev => ({...prev, enum: e.target.value}))}
                              size="sm"
                              flex="2"
                            />
                            <Input
                              placeholder="Format (e.g., email, date)"
                              value={parameterForm.format}
                              onChange={(e) => setParameterForm(prev => ({...prev, format: e.target.value}))}
                              size="sm"
                              flex="1"
                            />
                            <Button size="sm" onClick={addParameter} colorScheme="blue">
                              Add
                            </Button>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>

                    {/* Parameters List */}
                    {Object.keys(functionForm.parameters.properties).length > 0 && (
                      <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                        {Object.entries(functionForm.parameters.properties).map(([name, param]) => (
                          <HStack key={name} justify="space-between" p={2} bg="white" borderRadius="md" mb={1}>
                            <VStack align="start" spacing={0} flex="1">
                              <HStack>
                                <Text fontWeight="bold" fontSize="sm">{name}</Text>
                                <Badge size="sm" colorScheme="blue">{param.type}</Badge>
                                {functionForm.parameters.required.includes(name) && (
                                  <Badge size="sm" colorScheme="red">Required</Badge>
                                )}
                                {param.default && (
                                  <Badge size="sm" colorScheme="gray">Default: {param.default}</Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="gray.600">{param.description}</Text>
                              {param.enum && (
                                <Text fontSize="xs" color="purple.600">Options: {param.enum.join(', ')}</Text>
                              )}
                            </VStack>
                            <IconButton
                              size="xs"
                              icon={<FaTrash />}
                              onClick={() => removeParameter(name)}
                              variant="ghost"
                              colorScheme="red"
                            />
                          </HStack>
                        ))}
                      </Box>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>

              {/* Implementation */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left" fontWeight="bold">
                    <Icon as={FaCode} mr={2} />
                    Implementation
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                  <VStack spacing={4} align="stretch">
                    <Alert status="info">
                      <AlertIcon />
                      <Text fontSize="sm">
                        Write JavaScript code that takes 'params' as input and returns a result object. 
                        You have access to 'context' (conversation context) and 'memory' (previous calls).
                      </Text>
                    </Alert>
                    <Textarea
                      value={functionForm.implementation}
                      onChange={(e) => setFunctionForm(prev => ({...prev, implementation: e.target.value}))}
                      placeholder={`// Enhanced implementation with Vapi features:
async function executeFunction(params, context, memory) {
  try {
    // Your function logic here
    const result = await processRequest(params);
    
    // Return structured response
    return {
      success: true,
      message: "Function executed successfully",
      data: result,
      speak: "The operation completed successfully",
      context_update: { last_action: "function_executed" }
    };
  } catch (error) {
    return {
      success: false,
      message: error.message,
      speak: "Sorry, there was an error processing your request",
      retry: true
    };
  }
}

return executeFunction(params, context, memory);`}
                      rows={12}
                      fontFamily="mono"
                      fontSize="sm"
                    />
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={saveFunction}>
              {editingFunction ? 'Update Function' : 'Create Function'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

// Test Function Modal (simplified for space)
const TestFunctionModal = ({ func, onTest }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [testParams, setTestParams] = useState({});

  const handleTest = () => {
    onTest(func, testParams);
    onClose();
  };

  return (
    <>
      <IconButton 
        size="xs" 
        icon={<FaPlay />} 
        onClick={onOpen}
        variant="ghost"
        colorScheme="green"
      />
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Test Function: {func.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">{func.description}</Text>
              
              <Box>
                <Text fontWeight="bold" mb={2}>Parameters</Text>
                {Object.entries(func.parameters.properties).map(([name, param]) => (
                  <FormControl key={name} mb={3}>
                    <FormLabel fontSize="sm">
                      {name}
                      {func.parameters.required.includes(name) && (
                        <Badge ml={2} colorScheme="red" size="sm">Required</Badge>
                      )}
                    </FormLabel>
                    <Input
                      placeholder={param.description}
                      value={testParams[name] || ''}
                      onChange={(e) => setTestParams(prev => ({...prev, [name]: e.target.value}))}
                      type={param.type === 'number' ? 'number' : 'text'}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Type: {param.type} {param.format && `| Format: ${param.format}`}
                    </Text>
                  </FormControl>
                ))}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="green" onClick={handleTest} leftIcon={<Icon as={FaPlay} />}>
              Test Function
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FunctionBuilder;